import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Chat, { ChatMessage } from "../../components/Chat";
import ChatComposer from "../../components/ChatComposer";
import ncludeLogo from "../../assets/nclude-color.svg";
import { BenefitsAuthenticationError, benefitsAuthenticatedFetch } from "../../utils/benefitsConversations";
import { ASK_BENEFITS_BOT } from "../../utils/constants";
import {
  clearBenefitsSession,
  getBenefitsConversationId,
  isBenefitsConversationId,
} from "../../utils/benefitsSession";
import "./BenefitsExperience.css";

const WELCOME_MESSAGE =
  "Hi! I’m the NClude Benefits Assistant. I’ll ask about your household, work, and income, then put together a report of programs you may qualify for. It takes about eight minutes, and you can stop at any time.";

function InfoAccess() {
  const navigate = useNavigate();
  const { conversationId: routeConversationId } = useParams();
  const pageTitleRef = useRef<HTMLHeadingElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const liveRegionRef = useRef<HTMLDivElement>(null);
  const [chat, setChat] = useState<ChatMessage[]>([
    {
      text: WELCOME_MESSAGE,
      timestamp: new Date().toISOString(),
      sender: "bot",
    },
  ]);
  const [prompt, setPrompt] = useState("");
  const [sendDisabled, setSendDisabled] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [conversationId] = useState(() =>
    isBenefitsConversationId(routeConversationId)
      ? routeConversationId
      : getBenefitsConversationId()
  );

  useEffect(() => {
    document.title = "Benefits Assistant | NClude";
    document.body.classList.add("benefits-chat-open");
    pageTitleRef.current?.focus();

    return () => document.body.classList.remove("benefits-chat-open");
  }, []);

  const appendAssistantChunk = (chunk: string, isFirstChunk: boolean) => {
    if (!chunk) return;

    if (isFirstChunk) {
      setIsStreaming(true);
      setChat((currentChat) => [
        ...currentChat,
        {
          text: chunk,
          timestamp: new Date().toISOString(),
          sender: "bot",
        },
      ]);
      return;
    }

    setChat((currentChat) => {
      const updatedChat = [...currentChat];
      const latestMessage = updatedChat[updatedChat.length - 1];
      updatedChat[updatedChat.length - 1] = {
        ...latestMessage,
        text: latestMessage.text + chunk,
      };
      return updatedChat;
    });
  };

  const sendChatMessage = async (messageText: string) => {
    try {
      const response = await benefitsAuthenticatedFetch(ASK_BENEFITS_BOT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: messageText,
          uuid: conversationId,
          stream: true,
        }),
      });

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }
      if (!response.body) {
        throw new Error("The response did not include a message stream");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let isFirstChunk = true;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        appendAssistantChunk(chunk, isFirstChunk);
        if (chunk) isFirstChunk = false;
      }

      const finalChunk = decoder.decode();
      appendAssistantChunk(finalChunk, isFirstChunk);
    } catch (error) {
      if (error instanceof BenefitsAuthenticationError) {
        clearBenefitsSession();
        navigate("/start", { replace: true });
        return;
      }

      console.error("Error fetching Benefits Assistant response:", error);
      setChat((currentChat) => [
        ...currentChat,
        {
          text: "I’m sorry, I couldn’t complete that response. Please try again in a moment.",
          timestamp: new Date().toISOString(),
          sender: "bot",
        },
      ]);
    } finally {
      setSendDisabled(false);
      setIsStreaming(false);
    }
  };

  const isInitialGreeting = chat.length === 1 && chat[0].sender === "bot";

  const handleGetStarted = () => {
    if (sendDisabled || isStreaming) return;
    const messageText = "Lets start";
    setSendDisabled(true);
    setChat((currentChat) => [
      ...currentChat,
      {
        text: messageText,
        timestamp: new Date().toISOString(),
        sender: "user",
      },
    ]);
    sendChatMessage(messageText);
  };

  const handlePromptSubmit = () => {
    if (isInitialGreeting) {
      handleGetStarted();
      return;
    }

    const messageText = prompt.trim();
    if (!messageText || sendDisabled) return;

    setSendDisabled(true);
    setChat((currentChat) => [
      ...currentChat,
      {
        text: messageText,
        timestamp: new Date().toISOString(),
        sender: "user",
      },
    ]);
    setPrompt("");
    sendChatMessage(messageText);
  };

  const renderMessageActions = (message: ChatMessage, index: number) => {
    if (isInitialGreeting && index === 0 && message.sender === "bot") {
      return (
        <div className="benefits-message-actions">
          <button
            type="button"
            className="benefits-get-started-btn"
            onClick={handleGetStarted}
            disabled={sendDisabled || isStreaming}
            aria-label="Get started with Benefits screening"
          >
            {sendDisabled ? "Starting…" : "Get started"}
          </button>
        </div>
      );
    }
    return null;
  };

  const finishSession = () => {
    clearBenefitsSession();
    navigate("/start", { replace: true });
  };

  return (
    <div className="benefits-agent-page">
      <header className="benefits-agent-header">
        <img src={ncludeLogo} alt="NClude" />
        <span className="benefits-agent-divider" aria-hidden="true" />
        <span className="benefits-agent-label">Benefits</span>
        <button type="button" className="benefits-agent-sign-out" onClick={finishSession}>
          Finish session
        </button>
      </header>

      <main className="benefits-agent-chat" id="main-content">
        <h1 className="benefits-agent-title" ref={pageTitleRef} tabIndex={-1}>
          NClude Benefits Assistant
        </h1>

        <div className="chat-window-container" aria-busy={sendDisabled}>
          <Chat
            chat={chat}
            isStreaming={isStreaming}
            assistantName="Benefits Assistant"
            announcementRef={liveRegionRef}
            renderMessageActions={renderMessageActions}
          />
          {sendDisabled && !isStreaming && (
            <p className="benefits-agent-thinking" role="status">
              Benefits Assistant is thinking…
            </p>
          )}
        </div>

        <div
          ref={liveRegionRef}
          className="benefits-agent-live-region"
          aria-live="polite"
          aria-atomic="true"
        />

        <footer className="benefits-agent-footer">
          <ChatComposer
            inputRef={inputRef}
            value={prompt}
            onChange={setPrompt}
            onSubmit={handlePromptSubmit}
            disabled={isInitialGreeting || sendDisabled}
            busy={sendDisabled}
            placeholder={isInitialGreeting ? "Click 'Get started' to begin" : "Type your answer…"}
            assistantName="Benefits Assistant"
            buttonText={sendDisabled ? "Sending…" : "Send"}
            helpText={isInitialGreeting ? "Please click 'Get started' above to begin your screening." : ""}
          />
          <p className="benefits-screening-disclaimer">
            This is a screening, not an application. Nothing is submitted to a benefits office.
          </p>
        </footer>
      </main>
    </div>
  );
}

export default InfoAccess;
